import { useEffect, useMemo, useRef, useState } from 'react'
import Picker from 'react-mobile-picker'
import { useTranslation } from 'react-i18next'
import { hapticImpact } from '../lib/haptics'

// ── Pure helpers (exported for unit tests) ─────────────────────────────────

// English fallback used only if the i18n bundle is somehow unavailable
// (e.g. a bundle load failure) — the component itself renders the
// locale-specific names from onboarding.months (see i18n/{en,id}.json).
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Number of days in a given month (1-indexed month). */
export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

/** Parse a 'YYYY-MM-DD' string into { year, month, day }, or null if invalid. */
export function parseBirthdate(str: string): { year: number; month: number; day: number } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) return null
  const [y, m, d] = str.split('-').map(Number)
  if (m < 1 || m > 12 || d < 1 || d > 31) return null
  const date = new Date(y, m - 1, d)
  // Detect day overflow (e.g. Feb 30 → Mar 2).
  if (date.getMonth() !== m - 1) return null
  return { year: y, month: m, day: d }
}

/** Format numbers into a 'YYYY-MM-DD' string. */
export function formatBirthdate(year: number, month: number, day: number): string {
  return [year, String(month).padStart(2, '0'), String(day).padStart(2, '0')].join('-')
}

/** Internal picker selection represented as string values (matching Picker item values).
 *  The index signature makes it assignable to react-mobile-picker's PickerValue. */
export interface PickerSel {
  month: string  // '1'–'12'
  day: string    // '1'–'31'
  year: string   // four-digit year string
  [key: string]: string
}

export interface ColumnLists {
  months: string[]
  days: string[]
  years: string[]
}

/**
 * Build the scrollable item lists for each column given the current selection
 * and allowed date range. Boundary years/months get clamped lists so the user
 * cannot scroll past minDate or maxDate.
 */
export function buildColumns(minDate: Date, maxDate: Date, sel: PickerSel): ColumnLists {
  const minY = minDate.getFullYear()
  const maxY = maxDate.getFullYear()
  const selY = Number(sel.year)
  const selM = Number(sel.month)

  // Years: ascending (oldest at top matches iOS convention)
  const years: string[] = []
  for (let y = minY; y <= maxY; y++) years.push(String(y))

  // Months: clamp for boundary years
  let mStart = 1
  let mEnd = 12
  if (selY === minY) mStart = minDate.getMonth() + 1
  if (selY === maxY) mEnd = maxDate.getMonth() + 1
  const months: string[] = []
  for (let m = mStart; m <= mEnd; m++) months.push(String(m))

  // Days: clamp to calendar month + boundary date limits
  let dStart = 1
  let dEnd = daysInMonth(selY, selM)
  if (selY === minY && selM === minDate.getMonth() + 1) dStart = minDate.getDate()
  if (selY === maxY && selM === maxDate.getMonth() + 1) dEnd = Math.min(dEnd, maxDate.getDate())
  const days: string[] = []
  for (let d = dStart; d <= dEnd; d++) days.push(String(d))

  return { months, days, years }
}

/**
 * Clamp month and day in `sel` if they fall outside the columns available for
 * the given date bounds. Call this after any column change to prevent
 * impossible dates (e.g. day 31 in February).
 */
export function clampSelection(sel: PickerSel, minDate: Date, maxDate: Date): PickerSel {
  const { months } = buildColumns(minDate, maxDate, sel)
  const month = months.includes(sel.month) ? sel.month : months[months.length - 1]

  const { days } = buildColumns(minDate, maxDate, { ...sel, month })
  const day = days.includes(sel.day) ? sel.day : days[days.length - 1]

  return { ...sel, month, day }
}

// ── Component ──────────────────────────────────────────────────────────────

interface WheelDatePickerProps {
  value: string            // 'YYYY-MM-DD' or '' (empty on first visit)
  onChange: (v: string) => void
  minDate: Date
  maxDate: Date
}

const ITEM_HEIGHT = 44
const VISIBLE_ITEMS = 5
const PICKER_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS  // 220px

function defaultSel(): PickerSel {
  return { month: '1', day: '1', year: String(new Date().getFullYear() - 25) }
}

export default function WheelDatePicker({ value, onChange, minDate, maxDate }: WheelDatePickerProps) {
  const { t } = useTranslation()
  const monthNames = t('onboarding.months', { returnObjects: true }) as string[]
  const [sel, setSel] = useState<PickerSel>(() => {
    const parsed = parseBirthdate(value)
    return parsed
      ? { month: String(parsed.month), day: String(parsed.day), year: String(parsed.year) }
      : defaultSel()
  })

  const columns = useMemo(() => buildColumns(minDate, maxDate, sel), [minDate, maxDate, sel])

  // When parent has no value yet (first visit), emit our default so the parent
  // state is valid and the "Next" button enables immediately.
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current) return
    seeded.current = true
    if (!parseBirthdate(value)) {
      onChange(formatBirthdate(Number(sel.year), Number(sel.month), Number(sel.day)))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: seed once on mount
  }, [])

  // Sync when the parent provides an externally updated value (e.g. edit mode
  // loads a saved birthdate after the component is already mounted).
  useEffect(() => {
    const parsed = parseBirthdate(value)
    if (!parsed) return
    setSel((prev) => {
      const next = { month: String(parsed.month), day: String(parsed.day), year: String(parsed.year) }
      // Bail out if nothing changed to avoid spurious re-renders.
      if (prev.month === next.month && prev.day === next.day && prev.year === next.year) return prev
      return next
    })
  }, [value])

  const prevSel = useRef(sel)

  function handleChange(newVal: PickerSel, key: string) {
    const clamped = clampSelection(newVal, minDate, maxDate)
    setSel(clamped)
    onChange(formatBirthdate(Number(clamped.year), Number(clamped.month), Number(clamped.day)))
    // Haptic tick only when the value in the changed column actually moved.
    if (prevSel.current[key as keyof PickerSel] !== clamped[key as keyof PickerSel]) {
      hapticImpact('light')
    }
    prevSel.current = clamped
  }

  // Compute displayed age from current selection.
  const age = useMemo(() => {
    const today = new Date()
    const y = Number(sel.year), m = Number(sel.month), d = Number(sel.day)
    let a = today.getFullYear() - y
    if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) a--
    return a
  }, [sel])

  const BAND_TOP = PICKER_HEIGHT / 2 - ITEM_HEIGHT / 2  // 88px

  return (
    <div>
      <div
        className="relative overflow-hidden rounded-xl bg-tg-secondary-bg"
        style={{
          height: PICKER_HEIGHT,
          WebkitMaskImage:
            'linear-gradient(to bottom, transparent 0%, black 28%, black 72%, transparent 100%)',
          maskImage:
            'linear-gradient(to bottom, transparent 0%, black 28%, black 72%, transparent 100%)',
        }}
      >
        {/* Selection band — hairline top + bottom border at the center row */}
        <div
          className="pointer-events-none absolute inset-x-0 z-10 border-y border-tg-hint opacity-30"
          style={{ top: BAND_TOP, height: ITEM_HEIGHT }}
        />

        <Picker
          value={sel}
          onChange={handleChange}
          height={PICKER_HEIGHT}
          itemHeight={ITEM_HEIGHT}
          wheelMode="natural"
          className="h-full"
        >
          {/* Month */}
          <Picker.Column name="month">
            {columns.months.map((m) => (
              <Picker.Item key={m} value={m}>
                {({ selected }) => (
                  <div
                    className={`flex h-full items-center justify-center text-[15px] transition-colors ${
                      selected ? 'font-semibold text-tg-text' : 'text-tg-hint'
                    }`}
                  >
                    {monthNames[Number(m) - 1] ?? MONTH_NAMES[Number(m) - 1]}
                  </div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>

          {/* Day */}
          <Picker.Column name="day">
            {columns.days.map((d) => (
              <Picker.Item key={d} value={d}>
                {({ selected }) => (
                  <div
                    className={`flex h-full items-center justify-center text-[15px] transition-colors ${
                      selected ? 'font-semibold text-tg-text' : 'text-tg-hint'
                    }`}
                  >
                    {d}
                  </div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>

          {/* Year */}
          <Picker.Column name="year">
            {columns.years.map((y) => (
              <Picker.Item key={y} value={y}>
                {({ selected }) => (
                  <div
                    className={`flex h-full items-center justify-center text-[15px] transition-colors ${
                      selected ? 'font-semibold text-tg-text' : 'text-tg-hint'
                    }`}
                  >
                    {y}
                  </div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>
        </Picker>
      </div>

      {/* Age indicator below the picker */}
      <p className="mt-2 text-center text-sm text-tg-hint">
        {age >= 0 && age <= 120 ? t('onboarding.ageLabel', { age }) : ''}
      </p>
    </div>
  )
}
